import React, { useState } from 'react';
import { Money, Bank, Trash, CheckCircle } from 'phosphor-react';
import { formatVND, formatDate } from '_helpers';
import { Modal, StatusPill, Tag } from '_ui';

// Full breakdown of one invoice — every service line with unit price, quantity, line total,
// commission % and commission earned. This is the "see everything" view that makes the
// commission system transparent: the collapsed "×1 +2" summary in the lists links here.
//
// Props:
//   invoice        — a transaction row (with .items[], totals, method, status, ...)
//   onClose        — close handler
//   employeeName   — optional, shown when a manager opens an invoice from a mixed list
//   showCommission — show the commission columns (default true). Managers' all-employee
//                    cash/transfer lists can hide it; the employee's own view always shows it.
export function InvoiceDetail({ invoice, onClose, employeeName, showCommission = true, onDelete, onVerifyPaid }) {
    const r = invoice;
    const isCash = r?.paymentMethod === 'cash';
    const [confirming, setConfirming] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [verifying, setVerifying] = useState(false);

    const canDel = !!onDelete && !!r;
    const canVerify = !!onVerifyPaid && !!r && r.status !== 'paid';

    async function handleDelete() {
        if (!confirming) { setConfirming(true); return; }
        setDeleting(true);
        await onDelete(r._id);
        setDeleting(false);
        setConfirming(false);
    }

    async function handleVerify() {
        setVerifying(true);
        await onVerifyPaid(r._id);
        setVerifying(false);
    }

    return (
        <Modal open={!!r} onClose={() => { setConfirming(false); onClose(); }} size="lg" eyebrow="Hoá đơn"
            title={r?.customerName ? r.customerName : 'Khách lẻ'}>
            {r && (
                <div className="space-y-6">
                    {/* meta row */}
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-espresso-soft">
                        <span className="tnum">{formatDate(r.createdAt, true)}</span>
                        {employeeName && <span>NV: <span className="text-espresso">{employeeName}</span></span>}
                        <Tag tone="neutral">
                            <span className="inline-flex items-center gap-1.5">
                                {isCash ? <Money size={13} /> : <Bank size={13} />}
                                {isCash ? 'Tiền mặt' : 'Chuyển khoản'}
                            </span>
                        </Tag>
                        <StatusPill status={r.status} pendingLabel="Chờ thu" paidLabel="Đã thu" />
                        {r.transferCode && (
                            <span>Mã CK: <span className="tnum text-espresso">{r.transferCode}</span></span>
                        )}
                    </div>

                    {/* line items */}
                    <div className="overflow-x-auto rounded-squircle-inner ring-1 ring-espresso/[0.06]">
                        <table className="w-full border-collapse text-sm">
                            <thead>
                                <tr className="text-[11px] font-semibold uppercase tracking-[0.12em] text-espresso-soft/70">
                                    <th className="px-4 py-3 text-left">Dịch vụ</th>
                                    <th className="px-4 py-3 text-right">Đơn giá</th>
                                    <th className="px-4 py-3 text-right">SL</th>
                                    <th className="px-4 py-3 text-right">Thành tiền</th>
                                    {showCommission && <th className="px-4 py-3 text-right">HH%</th>}
                                    {showCommission && <th className="px-4 py-3 text-right">Hoa hồng</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {r.items.map((it, i) => (
                                    <tr key={i} className="border-t border-espresso/[0.06]">
                                        <td className="px-4 py-3 text-espresso">{it.name}</td>
                                        <td className="px-4 py-3 text-right tnum text-espresso-soft">{formatVND(it.price)}</td>
                                        <td className="px-4 py-3 text-right tnum text-espresso-soft">{it.quantity}</td>
                                        <td className="px-4 py-3 text-right tnum font-medium text-espresso">{formatVND(it.lineTotal)}</td>
                                        {showCommission && <td className="px-4 py-3 text-right tnum text-espresso-soft">{it.commissionRate}%</td>}
                                        {showCommission && <td className="px-4 py-3 text-right tnum text-sage-deep">{formatVND(it.commissionEarned)}</td>}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="border-t border-espresso/[0.12] font-medium">
                                    <td className="px-4 py-3 text-espresso" colSpan={3}>Tổng cộng</td>
                                    <td className="px-4 py-3 text-right tnum text-espresso">{formatVND(r.totalPrice)}</td>
                                    {showCommission && <td className="px-4 py-3" />}
                                    {showCommission && <td className="px-4 py-3 text-right tnum text-sage-deep">{formatVND(r.totalCommission)}</td>}
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    {(canDel || canVerify) && (
                        <div className="flex justify-end gap-2 pt-2">
                            {canVerify && (
                                <button
                                    onClick={handleVerify}
                                    disabled={verifying}
                                    className="inline-flex items-center gap-2 rounded-squircle px-4 py-2 text-sm font-medium text-sage-deep ring-1 ring-sage-deep/30 hover:bg-sage-deep/10 disabled:opacity-50"
                                >
                                    <CheckCircle size={14} />
                                    {verifying ? 'Đang xác nhận…' : 'Xác nhận đã thu (thủ công)'}
                                </button>
                            )}
                            {canDel && (
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className={`inline-flex items-center gap-2 rounded-squircle px-4 py-2 text-sm font-medium transition-colors ${
                                        confirming
                                            ? 'bg-red-600 text-white hover:bg-red-700'
                                            : 'text-red-600 ring-1 ring-red-300 hover:bg-red-50'
                                    } disabled:opacity-50`}
                                >
                                    <Trash size={14} />
                                    {deleting ? 'Đang xoá…' : confirming ? 'Xác nhận xoá?' : 'Xoá giao dịch'}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}
